import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, HammerGestureConfig, HammerModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DefaultUrlSerializer, RouterModule, UrlSerializer, UrlTree } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Hammer from 'hammerjs';
import { NgxCurrencyModule } from 'ngx-currency';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { PerfectScrollbarConfigInterface, PerfectScrollbarModule, PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { QuillModule } from 'ngx-quill';
import { ReplaceLineBreaksPipeModule } from '../app/shared/pipe/nl2br-pipe/nl2br-pipe.modules';
import { environment } from '../environments/environment';
import { HeaderService } from '../services/header.service';
import { ProfileService } from './../services/profile.service';
import { SharedService } from './../services/shared.service';
import { AppComponent } from './app.component';
import { AppRoutes } from './app.routing';
import { DataService } from './data.service';
import { DemoMaterialModule } from './demo-material-module';
import { GraphQLModule } from './graphql.module';
import { AppBlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { AppSidebarComponent } from './layouts/full/sidebar/sidebar.component';
import { SubheaderComponent } from './layouts/full/subheader/subheader.component';
import { ActivityDetailDialog } from './shared/component/dialog/activity-detail-dialog/activity-detail-dialog';
import { AttachmentDialog } from './shared/component/dialog/attachment-dialog/attachment-dialog';
import { ConfirmationDialog } from './shared/component/dialog/confirmation-dialog/confirmation-dialog';
import { EditServiceRequestDialog } from './shared/component/dialog/edit-service-request-dialog/edit-service-request-dialog';
import { SRListingFilterDialog } from './shared/component/dialog/sr-listing-filter-dialog/sr-listing-filter-dialog';
import { HistoryDialogComponent } from './shared/component/dialog/history-dialog/history-dialog';
import { MeterAttachmentDialog } from './shared/component/dialog/meter-attachment-dialog/meter-attachment-dialog';
import { PopupDialogComponent } from './shared/component/dialog/popup-dialog/popup-dialog';
import { SelectionDialogComponent } from './shared/component/dialog/selection-dialog/selection-dialog';
import { TenantSalesFilterDialog } from './shared/component/dialog/tenant-sales-filter-dialog/tenant-sales-filter-dialog';
import { SanitizerPipeModule } from './shared/pipe/sanitizer-pipe/sanitizer-pipe.module';
import { SpinnerComponent } from './shared/spinner.component';
import { CarouselModule } from 'ngx-owl-carousel-o';
import {NgxMatIntlTelInputModule} from 'ngx-mat-intl-tel-input';
import { ACLService } from '../services/acl.service';
import { CryptoService } from '../services/crypto.service';
import { SocketIoService } from '../services/socketio.service';
import { DialogHeader } from './template/dialog/dialog-header-template/dialog-header';
import { Dialog1Template } from './template/dialog/dialog1-template/dialog1-template';
import { Dialog2Template } from './template/dialog/dialog2-template/dialog2-template';
import { Dialog3Template } from './template/dialog/dialog3-template/dialog3-template';
import { Dialog4Template } from './template/dialog/dialog4-template/dialog4-template';
import { Dialog5Template } from './template/dialog/dialog5-template/dialog5-template';
import { Dialog6Template } from './template/dialog/dialog6-template/dialog6-template';
import { FooterService } from '../services/footer.service';
import { DynamicPipeModule } from './template/pipe/dynamic-pipe.module';
import { FooterComponent } from './layouts/full/footer/footer.component';
import { PopupMenuComponent } from './layouts/full/popup-menu/popup-menu.component';
import { CustomDirectiveModule } from './shared/directive/directive.module';
import { TextMiddleEllipsisPipeModule } from './template/pipe/textMiddleEllipsis-pipe.module';
import { ConfirmDeleteNewsDialog } from './shared/component/dialog/confirm-delete-news-dialog/confirm-delete-news-dialog';

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    overrides = {
        // override hammerjs default configuration
        swipe: { direction: Hammer.DIRECTION_ALL }
    } as any;
}

export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const subStr = url.split("?");

        if (subStr.length > 1) {
            // only make url before query param to lower case
            // all query params remain allow CAPS
            return super.parse(subStr[0].toLowerCase() + "?" + subStr[1]);
        }

        return super.parse(url.toLowerCase());
    }
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true,
    wheelSpeed: 2,
    wheelPropagation: true
};

@NgModule({
    entryComponents: [
        PopupDialogComponent,
        ConfirmationDialog,
        SelectionDialogComponent,
        AttachmentDialog,
        MeterAttachmentDialog,
        ActivityDetailDialog,
        SRListingFilterDialog,
        EditServiceRequestDialog,
        HistoryDialogComponent,
        TenantSalesFilterDialog
    ],
    declarations: [
        AppComponent,
        AppBlankComponent,
        AppSidebarComponent,
        SpinnerComponent,
        FullComponent,
        SubheaderComponent,
        PopupDialogComponent,
        ConfirmationDialog,
        MeterAttachmentDialog,
        SelectionDialogComponent,
        AttachmentDialog,
        ActivityDetailDialog,
        SRListingFilterDialog,
        EditServiceRequestDialog,
        HistoryDialogComponent,
        TenantSalesFilterDialog,
        Dialog1Template,
        Dialog2Template,
        Dialog3Template,
        Dialog4Template,
        Dialog5Template,
        Dialog6Template,
        DialogHeader,
        FooterComponent,
        PopupMenuComponent,
        ConfirmDeleteNewsDialog

    ],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        DemoMaterialModule,
        NgxCurrencyModule,
        FormsModule,
        FlexLayoutModule,
        HttpClientModule,
        PerfectScrollbarModule,
        ReactiveFormsModule,
        HammerModule,
        SanitizerPipeModule,
        QuillModule.forRoot({ theme: 'snow' }),
        RouterModule.forRoot(AppRoutes, { scrollPositionRestoration: 'top' }),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        GraphQLModule,
        NgxMaterialTimepickerModule,
        ReplaceLineBreaksPipeModule,
        CarouselModule,
        NgxMatIntlTelInputModule,
        CustomDirectiveModule,
        DynamicPipeModule,
        TextMiddleEllipsisPipeModule
    ],
    providers: [
        {
            provide: PERFECT_SCROLLBAR_CONFIG,
            useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
        },
        SharedService,
        HeaderService,
        ProfileService,
        ACLService,
        CryptoService,
        SocketIoService,
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        },
        DataService,
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer
        },
        FooterService
    ],

    bootstrap: [AppComponent]
})
export class AppModule { }
